// React and hooks
import React, { useState, useEffect } from 'react';

// Icons
import { Search, X } from 'lucide-react';

// API client
import api from '../../lib/api';

// Theme hook
import useTheme from '../../hooks/useTheme';

// Modal
import AddProductWeightModal from './AddProductWeightModal';

// i18n
import { useTranslation } from 'react-i18next';

const AddExitModal = ({ isOpen, onClose, onCreated, locationKey, selectedDate }) => {
  // States
  const [products, setProducts] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [quantity, setQuantity] = useState('');
  const [notes, setNotes] = useState('');
  const [showWeightModal, setShowWeightModal] = useState(false);
  const [search, setSearch] = useState('');

  // Hooks
  const { theme } = useTheme();
  const { t } = useTranslation();

  // Base button styles
  const baseBtn =
    theme === 'dark'
      ? "inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold shadow transition duration-200 bg-white text-black hover:bg-black hover:text-white hover:scale-105"
      : "inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold shadow transition duration-200 bg-gray-900 text-white hover:bg-white hover:text-black hover:scale-105";

  // Helpers
  const getId = (obj) => obj?.id || obj?._id || obj?.productId;
  const currentInventory = selected
    ? inventory.find((item) => item.productId === getId(selected))
    : null;
  const currentBoxes = Number(currentInventory?.totalBoxes || 0);
  const currentWeight = Number(currentInventory?.totalWeight || 0);
  const exitingBoxes = selected?.hasBaseWeight ? (Number(quantity) || 0) : 0;
  const exitingWeight = exitingBoxes * Number(selected?.fixedWeight || 0);

  // Load inventory summaries when modal opens
  useEffect(() => {
    if (!isOpen) return;

    const loadData = async () => {
      try {
        const [sumRes, brandRes, catRes] = await Promise.all([
          // Inventory summary by location
          api.get(`/inventory/summary/products?location=${locationKey}`),
          api.get('/brands'),
          api.get('/categories'),
        ]);

        const summaries = Array.isArray(sumRes.data) ? sumRes.data : [];
        setInventory(summaries);

        // Only show products with stock
        const withStock = summaries.filter(
          (s) => (s.totalBoxes ?? 0) > 0 || (s.totalWeight ?? 0) > 0
        );

        setProducts(withStock);
        setBrands(Array.isArray(brandRes.data) ? brandRes.data : []);
        setCategories(Array.isArray(catRes.data) ? catRes.data : []);
        try {
          const clientRes = await api.get('/clients');
          setClients(Array.isArray(clientRes.data) ? clientRes.data : []);
        } catch (clientError) {
          console.error('Failed to load clients for inventory exit', clientError);
          setClients([]);
        }
      } catch (err) {
        console.error('Failed to load inventory summaries', err);
      }
    };

    loadData();
  }, [isOpen, locationKey]);

  // Reset form values
  const resetForm = () => {
    setSelected(null);
    setQuantity('');
    setNotes('');
    setSelectedClientId('');
    setClientSearch('');
    setSearch('');
    setShowWeightModal(false);
  };

  // Close modal
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Resolve brand name
  const brandName = (p) => {
    const brand = brands.find((b) => getId(b) === (p.brandId || p.brand?._id));
    return brand ? brand.name : '-';
  };

  // Resolve category name
  const categoryName = (p) => {
    const cat = categories.find((c) => getId(c) === (p.categoryId || p.category?._id));
    return cat ? cat.name : '-';
  };

  // Render product label
  const renderLabel = (p) => {
    const supplier = p.supplierName || p.supplier || '-';
    const category = p.categoryName || categoryName(p);
    return `${p.name} — ${t('brand')}: ${brandName(p)} — ${t('category')}: ${category} — ${t('supplier')}: ${supplier}`;
  };

  // Select product and normalize id
  const handleSelectProduct = async (p) => {
    try {
      const { data } = await api.get(`/products/${getId(p)}`);

      // Ensure product has a normalized id field
      setSelected({
        ...data,
        id: getId(data),
      });
    } catch {
      setSelected({
        ...p,
        id: getId(p),
      });
    }

    // Reset weight modal when changing product
    setShowWeightModal(false);
  };

  // Save exit
  const handleSave = async () => {
    if (!selected) return;

    try {
      const id = getId(selected);

      // Fixed-weight products (boxes)
      if (selected.hasBaseWeight) {
        if (!quantity || Number(quantity) <= 0) {
          alert(t('errorEnterQuantity'));
          return;
        }

        await api.post(`/inventory/exits/${id}?location=${locationKey}`, {
          quantity: Number(quantity),
          notes,
          clientId: selectedClientId || null,
          clientName: clients.find((client) => String(getId(client)) === String(selectedClientId))?.name || null,
          date: selectedDate,
        });

        if (onCreated) onCreated();
        handleClose();
      } else {
        // Variable-weight products
        setShowWeightModal(true);
      }
    } catch (err) {
      console.error(err);

      if (err.response?.data?.error) {
        alert(err.response.data.error);
      } else {
        alert(t('errorRegisterExit'));
      }
    }
  };

  // Filter products
  const term = search.toLowerCase();
  const filtered = products.filter(
    (p) =>
      p.name?.toLowerCase().includes(term) ||
      brandName(p).toLowerCase().includes(term) ||
      (p.categoryName || categoryName(p)).toLowerCase().includes(term) ||
      (p.supplierName || p.supplier || '').toLowerCase().includes(term)
  );
  const filteredClients = clients.filter((client) =>
    String(client.name || '').toLocaleLowerCase().includes(clientSearch.trim().toLocaleLowerCase())
  );

  if (!isOpen) return null;

  // Render
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50 p-4">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md w-full max-w-md space-y-4 relative">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 text-gray-600 dark:text-gray-300 hover:text-red-600"
        >
          <X size={20} />
        </button>

        {/* Title */}
        <h2 className="text-xl font-bold text-gray-800 dark:text-white">
          {t('newExit')}
        </h2>

        {/* Search */}
        <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg px-3 py-2">
          <Search size={16} className="text-gray-500 dark:text-gray-300 mr-2" />
          <input
            type="text"
            placeholder={t('searchProduct')}
            className="bg-transparent outline-none flex-1 text-sm text-gray-700 dark:text-gray-200"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Product list */}
        <div className="max-h-40 overflow-y-auto border rounded-md">
          {filtered.length > 0 ? (
            filtered.map((p) => (
              <button
                key={getId(p)}
                onClick={() => handleSelectProduct(p)}
                className={`block w-full text-left px-3 py-2 text-sm ${
                  getId(selected) === getId(p)
                    ? 'bg-rose-600 text-white'
                    : 'hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200'
                }`}
              >
                {renderLabel(p)}
              </button>
            ))
          ) : (
            <p className="px-3 py-2 text-sm text-gray-500">
              {t('noProductsFound')}
            </p>
          )}
        </div>

        {/* Quantity input for fixed-weight products */}
        {selected?.hasBaseWeight && (
          <input
            type="number"
            placeholder={t('quantity')}
            className="w-full border px-4 py-2 rounded-md dark:bg-gray-700 dark:text-white"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
          />
        )}

        {selected && (
          <div className="rounded-md bg-gray-100 dark:bg-gray-700 px-3 py-2 text-sm text-gray-700 dark:text-gray-200">
            <p>{t('availableNow')}: {currentBoxes} {t('boxes')} · {currentWeight.toFixed(2)} KG</p>
            <p className="font-semibold">{t('projectedAfterExit')}: {Math.max(0, currentBoxes - exitingBoxes)} {t('boxes')} · {Math.max(0, currentWeight - exitingWeight).toFixed(2)} KG</p>
          </div>
        )}

        {/* Notes */}
        {selected && (
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200">{t('exitClient')} <span className="font-normal text-gray-500">({t('optional')})</span></label>
            <div className="flex items-center rounded-lg border bg-white px-3 dark:border-gray-600 dark:bg-gray-700">
              <Search size={16} className="mr-2 shrink-0 text-gray-500 dark:text-gray-300" />
              <input type="search" value={clientSearch} onChange={(event) => setClientSearch(event.target.value)} placeholder={t('searchClient')} className="w-full bg-transparent py-2 text-sm text-gray-800 outline-none dark:text-gray-100" />
            </div>
            <div className="max-h-28 overflow-y-auto rounded-md border dark:border-gray-600">
              <button type="button" onClick={() => setSelectedClientId('')} className={`block w-full px-3 py-2 text-left text-sm ${!selectedClientId ? 'bg-blue-100 font-semibold dark:bg-blue-900/50' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}>{t('noClient')}</button>
              {filteredClients.map((client) => {
                const clientId = getId(client);
                return <button type="button" key={clientId} onClick={() => setSelectedClientId(String(clientId))} className={`block w-full px-3 py-2 text-left text-sm ${String(clientId) === selectedClientId ? 'bg-blue-100 font-semibold dark:bg-blue-900/50' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}>{client.name}</button>;
              })}
              {filteredClients.length === 0 && <p className="px-3 py-2 text-sm text-gray-500">{t('noClients')}</p>}
            </div>
          </div>
        )}

        {selected && (
          <textarea
            placeholder={t('notesPlaceholder')}
            className="w-full border px-3 py-2 rounded-md dark:bg-gray-700 dark:text-white text-sm"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        )}

        {/* Action buttons */}
        {selected && (
          <div className="flex justify-end gap-2">
            <button onClick={handleClose} className={baseBtn}>
              {t('cancel')}
            </button>
            <button onClick={handleSave} className={baseBtn}>
              {selected?.hasBaseWeight ? t('save') : t('next')}
            </button>
          </div>
        )}

        {/* Variable-weight modal */}
        {showWeightModal && selected && (
          <AddProductWeightModal
            isOpen={showWeightModal}
            onClose={() => setShowWeightModal(false)}
            product={selected}
            onUpdated={() => {
              if (onCreated) onCreated();
              handleClose();
            }}
            mode="exit"
            locationKey={locationKey}
            notes={notes}
            clientId={selectedClientId || null}
            clientName={clients.find((client) => String(getId(client)) === String(selectedClientId))?.name || null}
          />
        )}
      </div>
    </div>
  );
};

export default AddExitModal;
